import React, { useEffect, useState } from 'react'
import { superAxiosRequest } from 'axiosApi'
import { observer } from 'mobx-react'
import Stores from 'Stores'
import asideModalStore from 'Stores/asideModalStore'
import SelectCustom from 'components/ContentBox/Global/Select/SelectCustom'
import CounterOnline from 'components/ContentBox/Global/Counter/CounterOnline/CounterOnline'
import CheckboxToggle from 'components/ContentBox/Global/CheckboxToggle/CheckboxToggle'
import Flex from 'components/UI/Flex/Flex'
import FormField from 'components/UI/AsideModal/Form/FormField'
import FormLabel from 'components/UI/AsideModal/Form/FormLabel'
import FormInput from 'components/UI/AsideModal/Form/FormInput'
import CounterInput from 'components/UI/CounterInput/CounterInput'
import useLocalization from 'hooks/useLocalization'
import st from './GroupsForm.module.scss'
import { ReactComponent as GroupStatusIcon } from 'components/UI/icons/group-status-icon.svg'

export const formatOptionStatus = (opt) => (
  <span className={`${st.optionsStatus} ${st[`optionsStatus--${opt.id}`]}`}>
    <GroupStatusIcon />
    {opt.label}
  </span>
)

const GroupsForm = () => {
  const [catalog, setCatalog] = useState([false, []])
  const [responsible, setResponsible] = useState([false, []])
  const [locations, setLocations] = useState([])
  const {
    modalValues: modalValue,
    changeValue: changeValues,
    check,
  } = asideModalStore

  const { translate } = useLocalization({
    section: 'groups',
    subsection: 'group_form_modal',
    dict: {
      add_group: 'Add Group',
      add_new_group: 'Add New Group',
      company: 'Company',
      standard: 'Standard',
      mini: 'Mini group',
      one_on_one: 'One on one',
      select_company: 'Select Company',
      location: 'Location',
      select_location: 'Select Location',
      group_number: 'Group Number',
      group_name: 'Group Name',
      arbitrary_group_name: 'Arbitrary Group Name',
      type_capacity: 'Type & Capacity',
      discipline: 'Discipline',
      select_discipline: 'Select Discipline',
      category: 'Category',
      select_category: 'Select Category',
      level: 'Level',
      select_level: 'Select Level',
      status: 'Status',
      select_status: 'Select Status',
      format: 'Format',
      offline: 'Offline',
      online: 'Online',
      program_type: 'Program Type',
      select_program_type: 'Select Program Type',
      responsible: 'Responsible',
      select_responsible: 'Select Responsible',
      application_for_training: 'Application for Training',
      select_application_for_training: 'Select Application for Training',
      default_price: 'Default Price',
      select_default_price: 'Select Default Price',
      self_registration_in_Group: 'Self-Registration in a Group',
      modal_switch: 'Clients will be able to sign up for a group on their own',
      cancel: 'Cancel',
      save: 'Save',
    },
  })

  const sorter = (arr) => arr.sort((a, b) => (a.name > b.name ? 1 : -1))
  const typeData = [
    { id: 3, name: translate?.standard },
    { id: 1, name: translate?.mini },
    { id: 2, name: translate?.one_on_one },
  ]
  const typeValue = {
    3: 13,
    1: 6,
    2: 1,
  }

  const onChangeType = (type) => {
    changeValues(type.id || type.value, 'type')
    changeValues(typeValue[type.id] || typeValue[type.value], 'size')
  }

  const formatPriceOptionLabel = (opt, { context }) => {
    const formatPrice = (val) =>
      String(val).replace(/(\d)(?=(\d{3})+(\D|$))/g, '$1 ')

    const str = `${opt.name} ${context === 'menu' ? '<br/>' : ''}(<b>${formatPrice(opt.price_sum)}.00</b>тг. за ${opt.hours_sum} ${opt.hours_type.id === 1 ? 'ак.ч.' : 'астр.ч.'})`

    return <span dangerouslySetInnerHTML={{ __html: str }} />
  }

  useEffect(() => {
    let isMounted = true

    const apiConf = {
      method: 'get',
      link: 'groups/groups_catalog',
    }
    superAxiosRequest(apiConf)
      .then((response) => {
        if (isMounted) {
          setCatalog([true, response.data])
          const arr = response.data.employers.items.map((x) => ({
            id: x.id,
            name: Stores.transliterate(x.full_name),
          }))
          setResponsible([true, arr])
        }
      })
      .catch(() => {
        if (isMounted) {
          setCatalog([true, []])
        }
      })
    return () => {
      isMounted = false
    }

    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    let arr = catalog[0] ? catalog[1].locations.items : []
    if (modalValue.company)
      arr = arr.filter((i) => i.companies.includes(modalValue.company))
    setLocations(arr)
  }, [catalog, modalValue.company])

  return (
    <div className={st.groupForm}>
      <div className={st.fieldSet}>
        <FormField>
          <FormLabel isRequired>{translate?.company}</FormLabel>
          <SelectCustom
            placeholder={translate?.select_company}
            options={catalog[0] ? catalog[1].companies.items : []}
            sortKey='name'
            valueID={modalValue.company}
            onChange={(o) => changeValues(o.id, 'company')}
            isLoading={!catalog[0]}
            error={check('company')}
          />
        </FormField>
        <FormField>
          <FormLabel isRequired>{translate?.location}</FormLabel>
          <SelectCustom
            placeholder={translate?.select_location}
            options={locations}
            sortKey='name'
            valueID={modalValue.location}
            onChange={(o) => changeValues(o.id, 'location')}
            isLoading={!catalog[0]}
            isDisabled={!modalValue.company}
            error={check('location')}
          />
        </FormField>
        <FormField>
          <FormLabel isRequired>{translate?.group_number}:</FormLabel>
          <FormInput isError={check('number')}>
            <input
              placeholder={translate?.group_number}
              type='text'
              value={modalValue.number || ''}
              onChange={(e) => changeValues(e.target.value, 'number')}
            />
          </FormInput>
        </FormField>
        <FormField>
          <FormLabel isRequired>{translate?.group_name}:</FormLabel>
          <FormInput isError={check('name')}>
            <input
              placeholder={translate?.arbitrary_group_name}
              type='text'
              value={modalValue.name || ''}
              onChange={(e) => changeValues(e.target.value, 'name')}
            />
          </FormInput>
        </FormField>
        <FormField>
          <FormLabel isRequired>{translate?.type_capacity}:</FormLabel>
          <div className={st.group_row}>
            <SelectCustom
              // options={catalog[0] ? sorter(catalog[1].types.items) : []}
              options={typeData}
              valueID={modalValue.type}
              onChange={(o) => onChangeType(o)}
              error={check('type')}
            />
            <CounterInput
              value={modalValue.size || 0}
              min={1}
              max={modalValue.type === 2 ? 1 : 200}
              name='size'
              onChange={(e) => changeValues(e, 'size')}
            />
          </div>
        </FormField>
        <FormField>
          <FormLabel isRequired>{translate?.discipline}:</FormLabel>
          <SelectCustom
            placeholder={translate?.select_discipline}
            options={catalog[0] ? catalog[1].disciplines.items : []}
            sortKey='name'
            valueID={modalValue.discipline}
            onChange={(o) => changeValues(o.id, 'discipline')}
            isLoading={!catalog[0]}
            error={check('discipline')}
          />
        </FormField>
        <FormField>
          <FormLabel isRequired>{translate?.category}:</FormLabel>
          <SelectCustom
            placeholder={translate?.select_category}
            options={catalog[0] ? catalog[1].course_levels.items : []}
            valueID={modalValue.course_level}
            onChange={(o) => changeValues(o.id, 'course_level')}
            isLoading={!catalog[0]}
            error={check('course_level')}
          />
        </FormField>
        <FormField>
          <FormLabel isRequired>{translate?.level}:</FormLabel>
          <SelectCustom
            placeholder={translate?.select_level}
            options={catalog[0] ? sorter(catalog[1].skill_levels.items) : []}
            sortKey='name'
            valueID={modalValue.skill_level}
            onChange={(o) => changeValues(o.id, 'skill_level')}
            isLoading={!catalog[0]}
            isSearchable
            error={check('skill_level')}
          />
        </FormField>
        <FormField>
          <FormLabel isRequired>{translate?.responsible}:</FormLabel>
          <SelectCustom
            placeholder={translate?.select_responsible}
            options={responsible[0] ? responsible[1] : []}
            sortKey='name'
            valueID={modalValue.responsible}
            onChange={(o) => changeValues(o.id, 'responsible')}
            isLoading={!catalog[0]}
            isSearchable
            error={check('responsible')}
          />
        </FormField>
        <FormField>
          <FormLabel isRequired>{translate?.status}:</FormLabel>
          <SelectCustom
            placeholder={translate?.select_status}
            options={catalog[0] ? catalog[1].statuses.items : []}
            valueID={modalValue.status}
            onChange={(o) => changeValues(o.id, 'status')}
            isLoading={!catalog[0]}
            formatOptionLabel={formatOptionStatus}
            error={check('status')}
          />
        </FormField>
        <FormField>
          <FormLabel isRequired>{translate?.default_price}:</FormLabel>
          <SelectCustom
            placeholder={translate?.select_default_price}
            options={catalog[0] ? catalog[1].default_prices.items : []}
            getOptionLabel={(opt) =>
              opt.name +
              ' (' +
              opt.price_sum +
              'тг за ' +
              opt.hours_sum +
              (opt.hours_type.name === 'Академические'
                ? ' ак.ч.)'
                : ' астр.ч.)')
            }
            formatOptionLabel={formatPriceOptionLabel}
            sortKey='name'
            valueID={modalValue.default_price}
            onChange={(o) => changeValues(o.id, 'default_price')}
            isLoading={!catalog[0]}
            error={check('default_price')}
            isSearchable
          />
        </FormField>
        <FormField>
          <FormLabel isRequired>{translate?.format}:</FormLabel>
          <CounterOnline
            name='online'
            value={modalValue.online}
            onChange={changeValues}
          />
        </FormField>
        <FormField>
          <FormLabel isRequired>{translate?.program_type}:</FormLabel>
          <SelectCustom
            placeholder={translate?.select_program_type}
            options={catalog[0] ? catalog[1].program_types.items : []}
            valueID={modalValue?.program_type}
            onChange={(o) => changeValues(o.id, 'program_type')}
            error={check('program_type')}
            isLoading={!catalog[0]}
          />
        </FormField>
        <FormField>
          <FormLabel isRequired>
            {translate?.application_for_training}:
          </FormLabel>
          <SelectCustom
            placeholder={translate?.select_application_for_training}
            options={catalog[0] ? sorter(catalog[1].doc_types.items) : []}
            optionValueKey='type'
            optionLabelKey='type_description'
            valueID={modalValue.doc_type}
            onChange={(o) => changeValues(o.type, 'doc_type')}
            isLoading={!catalog[0]}
            error={check('doc_type')}
          />
        </FormField>
      </div>

      <FormField>
        <FormLabel>{translate?.self_registration_in_Group}:</FormLabel>
        <Flex>
          <CheckboxToggle
            right={translate?.modal_switch}
            checked={modalValue?.self_registration}
            onChange={() =>
              changeValues(!modalValue?.self_registration, 'self_registration')
            }
          />
        </Flex>
      </FormField>
    </div>
  )
}

export default observer(GroupsForm)
